import { useContext, useEffect, useMemo, useState } from 'react';
import {
  AgxButton,
  AgxRow,
  AgxColumn,
  AgxHeader,
  AgxLabel,
  Images,
  AgxBodyText,
  AgxSlideUpModal,
  AgxMultiOptionButton,
  DocumentTypesMap,
  DocumentTypes,
  StakeholderType,
  SendConfiguration,
  RenderAnimation,
  RenderAnimationType,
  AustralianState,
  MultiOptionButtonOption,
  FormConfigType,
  VendorBuyer,
} from '@urbanx/agx-ui-components';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useFormClearer } from 'hooks/useFormCleaner';
import { useFormSettings } from 'hooks/useFormSettings';
import usePropertyImageSelector from 'selectors/usePropertyImageSelector';
import useFormDocumentsSelector from 'selectors/useFormDocumentsSelector';
import useFormId from 'hooks/useFormId';
import useFormCampaignId from 'hooks/useFormCampaignId';
import ErrorModal from 'helpers/ErrorModal';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { markCampaignsAsStale } from '../../campaigns/campaignsReducer';
import { completeCampaignStep, sendForm } from 'Api/Campaigns/campaignsApi';
import { loadFormConfig } from '../../form/formReducer';
import { setAndShowErrorToast } from 'store/config';
import { PreparedSignatory, prepareSignatories } from 'utils/vendorBuyerUtil';
import placeHolderImage from 'assets/images/placeholder-property.png';
import SendConfirmation from './SendConfirmation';
import './ContractReviewAndSend.scss';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import { sortDocumentsByType, sortDocumentsByTypes } from 'utils/documentUtil';
import {
  completeMarketingPackageStep,
  sendMarketingPackage,
} from 'Api/Marketing/marketingApi';
import { FormType } from '@urbanx/agx.campaigns.client';

interface ContractReviewAndSignProps {
  goToFirstPage: () => void;
  goToPreviousPage: () => void;
  vendors?: VendorBuyer[];
  formName?: string;
  sendViaDocuSign: boolean;
  disableFormSubmission?: boolean;
  sendConfigurations: SendConfiguration[];
}

const ContractReviewAndSign = (props: ContractReviewAndSignProps) => {
  const {
    goToFirstPage,
    goToPreviousPage,
    vendors,
    formName,
    sendViaDocuSign,
    disableFormSubmission,
    sendConfigurations,
  } = props;
  const updateFormSettings = useFormSettings();
  const formId = useFormId();

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [buyerSignatories, setBuyerSignatories] = useState<PreparedSignatory[]>(
    []
  );
  const [vendorSignatories, setVendorSignatories] =
    useState<PreparedSignatory[]>();
  const [showDocuSignLoader, setShowDocuSignLoader] = useState(false);
  const [showSuccessLoader, setShowSuccessLoader] = useState(false);
  const isMobile = ScreenSize() === Breakpoints.Mobile;
  const isDesktop = ScreenSize() === Breakpoints.Desktop;
  const { formErrors: errors, downloadDocumentTypes } = useAppSelector(
    state => state.form
  );
  const availableForms = useAppSelector(state => state.forms.availableForms);
  const { medium } = usePropertyImageSelector() || {};
  const documents = useFormDocumentsSelector();
  const getFileLink = useGetFileLink();
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const clearFormData = useFormClearer();
  const [showRecipients, setShowRecipients] = useState(false);
  const [configuration, setConfiguration] = useState(sendConfigurations[0]);
  const [disableButtons, setDisableButtons] = useState(false);
  const [buyerSolicitorRecipient, setBuyerSolicitorRecipient] =
    useState<PreparedSignatory>();
  const [vendorSolicitorRecipient, setVendorSolicitorRecipient] =
    useState<PreparedSignatory>();

  const {
    selectedForm,
    formValues: {
      Buyers: campaignBuyers,
      BuyerSolicitor: buyerSolicitor,
      VendorSolicitor: vendorSolicitor,
    },
  } = useAppSelector(state => state.form);
  const campaignId = useFormCampaignId();

  const {
    agents: { loadingState: agentsLoaded, items: agents },
  } = useAppSelector(state => state.agencies);

  const noAttachments = useMemo(() => {
    const documentTypes = documents.map(doc => doc.documentType);
    const uniqueTypes = new Set(documentTypes);
    // Check if the unique types are one 'AgencyAgreement' and one 'MergedAgencyAgreement'
    return (
      uniqueTypes.size === 2 &&
      uniqueTypes.has(DocumentTypes.MergedSalesContract)
    );
  }, [documents]);

  const fileDownloadOptions = useMemo(() => {
    if (documents.length === 0) return [];
    const options: MultiOptionButtonOption[] = [];
    const hasCustomDownloads =
      downloadDocumentTypes && downloadDocumentTypes.length > 0;

    const filteredDocuments = documents.filter(
      document =>
        !hasCustomDownloads ||
        downloadDocumentTypes.includes(document.documentType)
    );

    const orderedDocuments = !hasCustomDownloads
      ? sortDocumentsByType(filteredDocuments)
      : sortDocumentsByTypes(filteredDocuments, downloadDocumentTypes);

    orderedDocuments.forEach(d => {
      options.push({
        text: d.documentType ? DocumentTypesMap[d.documentType as keyof typeof DocumentTypesMap] : '',
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    return options;
  }, [documents, getFileLink]);

  useEffect(() => {
    updateFormSettings({
      formBackgroundInverted: showDocuSignLoader || showSuccessLoader,
      growContent: showDocuSignLoader || showSuccessLoader,
    });
  }, [showDocuSignLoader, showSuccessLoader]);

  const beginSubmission = async () => {
    if (!getAuthToken || !campaignId || !formId) return;

    if (selectedForm?.formConfigType === FormConfigType.Campaign) {
      const contractSubmissionForm = availableForms.find(
        form =>
          form.type === FormType.ContractSubmission &&
          isEqual(form.state, selectedForm?.state)
      );

      if (contractSubmissionForm == null) {
        return;
      }
    }

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) {
        setDisableButtons(false);
        return;
      }

      const newForm =
        selectedForm?.formConfigType === FormConfigType.Campaign
          ? await completeCampaignStep(authToken, campaignId, true, formId)
          : await completeMarketingPackageStep(
              authToken,
              campaignId,
              formId,
              true
            );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Contract submission'));
        setDisableButtons(false);
        return;
      }

      dispatch(loadFormConfig(newForm));
    } catch (err) {
      dispatch(setAndShowErrorToast('Failed to start Contract submission'));
    }

    setDisableButtons(false);
  };

  const sendEnvelope = async (emailVendorNow: boolean) => {
    if (!getAuthToken || !campaignId) return;

    try {
      setShowDocuSignLoader(true);
      updateFormSettings({
        showBreadcrumbs: false,
        displayTitleVisible: false,
      });

      const authToken = await getAuthToken();

      if (!authToken) {
        updateFormSettings({
          showBreadcrumbs: true,
          displayTitleVisible: true,
        });
        setShowDocuSignLoader(false);
        return;
      }

      const sendFormSubmission = {
        campaignId,
        formId: formId || '',
        formType: selectedForm?.formType as FormType,
        state: selectedForm?.state as AustralianState,
        digitalSignatureSubmission: {
          sendVendorsNow: emailVendorNow,
        },
      };

      if (selectedForm?.formConfigType === FormConfigType.Marketing) {
        await sendMarketingPackage(authToken, sendFormSubmission);
      }

      if (selectedForm?.formConfigType === FormConfigType.Campaign) {
        await sendForm(authToken, sendFormSubmission);
      }

      setTimeout(() => {
        setShowDocuSignLoader(false);
        setShowSuccessLoader(true);
        setTimeout(() => {
          if (selectedForm?.formConfigType === FormConfigType.Campaign) {
            dispatch(markCampaignsAsStale());
          }

          clearFormData();
          navigate('/');
        }, 1800);
      }, 1500);
    } catch (err: any) {
      updateFormSettings({
        showBreadcrumbs: true,
        displayTitleVisible: true,
      });
      setShowDocuSignLoader(false);
      console.error(err);
      dispatch(setAndShowErrorToast(err.message));
    }
  };

  useEffect(() => {
    if (vendors) {
      setVendorSignatories(prepareSignatories(vendors, StakeholderType.Vendor));
    }
  }, [agents, agentsLoaded, vendors]);

  useEffect(() => {
    if (campaignBuyers) {
      setBuyerSignatories(
        prepareSignatories(campaignBuyers, StakeholderType.Buyer)
      );
    }
  }, [campaignBuyers]);

  useEffect(() => {
    if (buyerSolicitor) {
      setBuyerSolicitorRecipient({
        id: buyerSolicitor.solicitor?.solicitorName,
        name: buyerSolicitor.solicitor?.solicitorName,
        email: buyerSolicitor.solicitor?.solicitorEmail,
        type: StakeholderType.BuyerSolicitor,
      });
    }
  }, [buyerSolicitor]);

  useEffect(() => {
    if (vendorSolicitor) {
      setVendorSolicitorRecipient({
        id: vendorSolicitor.solicitor?.solicitorName,
        name: vendorSolicitor.solicitor?.solicitorName,
        email: vendorSolicitor.solicitor?.solicitorEmail,
        type: StakeholderType.VendorSolicitor,
      });
    }
  }, [vendorSolicitor]);

  const confirmationDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <Images.AlertCircleOutline />
      <AgxHeader size={3}>Starting a submission?</AgxHeader>
      <AgxBodyText medium>
        You will not be able to edit the {formName} once you continue.
      </AgxBodyText>
      <AgxBodyText medium>Continue to submission?</AgxBodyText>
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="startsubmission"
          large
          primary
          text="Yes, start submission"
          onClick={beginSubmission}
          disabled={disableButtons}
        />
        <AgxButton
          id="cancel"
          hollow
          large
          text="No, go back"
          onClick={() => setShowConfirmationModel(false)}
          disabled={disableButtons}
        />
      </div>
    </AgxColumn>
  );

  const viewPdfButton = () => {
    if (fileDownloadOptions.length === 0) return;

    if (fileDownloadOptions.length === 1 || noAttachments) {
      return (
        <AgxButton
          id="btn-download"
          text="Download"
          hollow
          medium
          onClick={fileDownloadOptions[0].onClick}
        />
      );
    } else {
      return (
        <AgxMultiOptionButton
          id="viewContractPdfs"
          text="Download"
          hollow
          medium
          options={fileDownloadOptions}
        />
      );
    }
  };

  const errorInfo = (
    <AgxRow centered veryLargeGap extraClasses={'toastStyle'} fill={isMobile}>
      <span>
        <Images.AlertCircle />
      </span>
      <AgxBodyText>
        {`Please complete required fields before ${sendViaDocuSign ? 'DocuSigning' : 'sending'}`}
        <br />
        <AgxButton
          id="btn-view-details"
          text="View Details"
          naked
          extraClasses={'hyperlink'}
          onClick={() => setOpenErrorModal(true)}
        ></AgxButton>
      </AgxBodyText>
    </AgxRow>
  );

  const onPreviousClicked = () => {
    goToPreviousPage();
  };

  if (showDocuSignLoader)
    return (
      <AgxColumn
        fill
        centerJustified
        centered
        extraClasses="sendForSigningStatusContent"
      >
        <RenderAnimation icon={RenderAnimationType.Paper} />
        <AgxColumn fill centered largeGap>
          <AgxHeader size={1} inverse>
            Just a sec
          </AgxHeader>
          <AgxHeader size={3} secondary>
            {`Sending ${formName}...`}
          </AgxHeader>
        </AgxColumn>
      </AgxColumn>
    );

  if (showSuccessLoader)
    return (
      <AgxColumn
        fill
        centerJustified
        centered
        extraClasses="sendForSigningStatusContent"
      >
        <RenderAnimation icon={RenderAnimationType.Champagne} />
        <AgxHeader size={1} inverse>
          Success!
        </AgxHeader>
      </AgxColumn>
    );

  if (showRecipients)
    return (
      <SendConfirmation
        buyers={buyerSignatories}
        buyerSolicitor={buyerSolicitorRecipient}
        vendors={vendorSignatories}
        vendorSolicitor={vendorSolicitorRecipient}
        onBack={() => setShowRecipients(false)}
        onSend={(sendVendorsNow: boolean) => sendEnvelope(sendVendorsNow)}
        sendViaDocuSign={sendViaDocuSign}
        sendConfiguration={configuration}
        isMobile={isMobile}
      />
    );

  return (
    <AgxColumn extraClasses="reviewAndSignImageCard">
      <AgxColumn
        veryLargeGap
        extraLargePadding
        centered
        extraClasses="agreementDetails"
      >
        <AgxHeader
          size={sendViaDocuSign ? 4 : 3}
          centered
        >{`Review ${formName} before proceeding:`}</AgxHeader>
        {sendViaDocuSign ? (
          <img
            className="propertyImage"
            alt="Avatar"
            src={medium || placeHolderImage}
          />
        ) : (
          <Images.SparkleForm />
        )}
        <AgxRow largeGap centered justifyCenter={isMobile} fill={isMobile}>
          {viewPdfButton()}
          <AgxButton
            id="btn-edit"
            text="Edit"
            onClick={goToFirstPage}
            hollow
            medium
            disabled={disableButtons}
          />
        </AgxRow>
      </AgxColumn>
      {sendViaDocuSign || sendConfigurations?.length > 1 ? (
        <div className="signingGrid">
          <AgxColumn largeGap centered extraClasses="signingGridCell">
            <AgxLabel large>DocuSign</AgxLabel>
            {sendConfigurations.map((config, index) => (
              <AgxButton
                id={`btn-${config.title}-${index}`}
                key={`btn-${config.title}-${index}`}
                primary
                large
                text={config.title}
                onClick={() => {
                  if (errors.length > 0) {
                    setOpenErrorModal(true);
                  } else {
                    setShowRecipients(true);
                    setConfiguration(config);
                  }
                }}
                disabled={disableButtons}
                wide
              />
            ))}
            {errors.length > 0 && errorInfo}
          </AgxColumn>
          {!disableFormSubmission && (
            <AgxColumn largeGap centered extraClasses="signingGridCell">
              <AgxLabel large>Sign in person</AgxLabel>
              <AgxButton
                id="btn-download-to-sign"
                primary
                large
                text="Download to Sign"
                onClick={
                  fileDownloadOptions.length > 0
                    ? fileDownloadOptions[0].onClick
                    : undefined
                }
                wide
                disabled={!fileDownloadOptions.length}
              />
              {fileDownloadOptions.length > 0 && (
                <AgxButton
                  id="btn-submit-signed-pdf"
                  hollow
                  large
                  text="Submit Signed PDF"
                  onClick={() => setShowConfirmationModel(true)}
                  disabled={disableButtons}
                  wide
                />
              )}
            </AgxColumn>
          )}
        </div>
      ) : (
        <>
          {errors.length > 0 && (
            <AgxColumn extraClasses={'alignColumn'}>{errorInfo}</AgxColumn>
          )}
          <div className="emailGrid borderTop">
            <AgxColumn centered>
              <AgxRow largeGap fill justifyCenter>
                <AgxButton
                  id="btn-review-back"
                  hollow
                  large
                  text="Back"
                  onClick={onPreviousClicked}
                />
                <AgxButton
                  id="btn-review-next"
                  primary
                  large
                  text="Next"
                  onClick={() => {
                    setShowRecipients(true);
                    setConfiguration(sendConfigurations[0]);
                  }}
                  disabled={errors.length > 0}
                />
              </AgxRow>
            </AgxColumn>
          </div>
        </>
      )}

      {openErrorModal && (
        <ErrorModal errorList={errors} setShowModal={setOpenErrorModal} />
      )}

      {showConfirmationModel && (
        <AgxSlideUpModal
          closeButton
          content={confirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowConfirmationModel(false)}
        />
      )}
    </AgxColumn>
  );
};

export default ContractReviewAndSign;
